import React from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form';
import type { ProyectoComunicacionDTO, ProyectoFormDTO } from '../../../../api/Objetos';
import { comunicacionApi, comunicacionesPublicasApi } from '../../../../api/api';
import { createEmptyProyecto } from '../emptyProyecto';
import { DateField, FieldGrid, NumberField, TextField, ToggleField } from '../../../../ui/form/fields';
import { FormSection, ItemCard, buildCardTitle, buildSummary, hasPersistedId, EmptyState, useProjectReadOnly, useProjectPublicView, BulkToggle } from '../projectForm.helpers';
import { SectionAlerts, SectionSaveButton, useCollectionSection } from '../sectionCrudUtils';
import { useRegisterSectionSave } from '../SectionSaveCoordinator';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';

export function ComunicacionSection(props: { proyectoId: number; showIds?: boolean }) {
  const publicView = useProjectPublicView();
  const readOnly = useProjectReadOnly();
  const form = useForm<ProyectoFormDTO>({ defaultValues: createEmptyProyecto() });
  const section = useCollectionSection({
    queryKey: [publicView ? 'public' : 'admin', 'proyectos', 'comunicacion'],
    proyectoId: props.proyectoId,
    getFn: publicView ? comunicacionesPublicasApi.listar : comunicacionApi.list,
    createFn: comunicacionApi.create,
    updateFn: comunicacionApi.update,
    deleteFn: comunicacionApi.delete,
  });

  React.useEffect(() => {
    const comunicaciones = (section.data ?? []).map((item: ProyectoComunicacionDTO) => ({
      ...item,
      fuente: item.fuente ?? item.lugar ?? '',
    }));
    form.reset({ ...createEmptyProyecto(), comunicacionProyecto: comunicaciones });
  }, [form, section.data]);

  const handleSave = form.handleSubmit(async (value) => {
    await section.save(value.comunicacionProyecto ?? []);
  });

  useRegisterSectionSave({
    id: 'comunicacion',
    isDirty: form.formState.isDirty,
    isSaving: section.saveMutation.isPending,
    save: async () => {
      await handleSave();
    },
  });

  return (
      <FormProvider {...form}>
        <ComunicacionSectionFields
            showIds={Boolean(props.showIds)}
            isLoading={section.isLoading}
            loadError={section.error}
        />
        {!readOnly && (
            <>
              <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending} />
            </>
        )}
      </FormProvider>
  );
}

function ComunicacionSectionFields(props: { showIds: boolean, isLoading: boolean, loadError: any }) {
  const { t } = useTranslation();
  const form = useFormContext<ProyectoFormDTO>();
  const comunicaciones = useFieldArray<ProyectoFormDTO, 'comunicacionProyecto'>({
    control: form.control,
    name: 'comunicacionProyecto',
  });
  const readOnly = useProjectReadOnly();

  const setAllPublic = (isPublic: boolean) => {
    comunicaciones.fields.forEach((field, index) => {
        form.setValue(`comunicacionProyecto.${index}.publico`, isPublic, { shouldDirty: true });
    });
  };

  const currentComunicaciones = form.watch('comunicacionProyecto') || [];

  return (
      <FormSection
          id="comunicacion"
          title={t('comunicacionSection.title', { count: comunicaciones.fields.length })}
          collapsible
          defaultOpen={false}
          headerActions={!readOnly &&
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  {props.isLoading && <BarraCargando size="1.2em" />}
                  <BulkToggle
                      label={t('comunicacionSection.public')}
                      fields={currentComunicaciones}
                      onToggle={setAllPublic}
                  />
              </div>
          }
      >
        <SectionAlerts loadError={props.loadError}/>
        {comunicaciones.fields.length === 0 && <EmptyState message={t('comunicacionSection.noCommunications')} />}
        {comunicaciones.fields.map((field, index) => (
            <ComunicacionFields
                key={field.id}
                index={index}
                showIds={props.showIds}
                onRemove={() => comunicaciones.remove(index)}
                defaultOpen={!hasPersistedId(form.watch(`comunicacionProyecto.${index}.id` as never))}
            />
        ))}

        {!readOnly ? (
            <button
                className="btn btnSecondary"
                type="button"
                onClick={() =>
                    comunicaciones.append({
                      titulo: '',
                      fecha: new Date().toISOString().slice(0, 10),
                      fuente: '',
                      enlaces: [],
                      publico: false,
                    } as ProyectoComunicacionDTO)
                }
            >
              {t('comunicacionSection.addCommunication')}
            </button>
        ) : null}
      </FormSection>
  );
}

function ComunicacionFields(props: { index: number; showIds: boolean; onRemove: () => void; defaultOpen?: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<ProyectoFormDTO>();
  const readOnly = useProjectReadOnly();
  const links = useWatch({ control: form.control, name: `comunicacionProyecto.${props.index}.enlaces` });
  const idComunicacion = useWatch({ control: form.control, name: `comunicacionProyecto.${props.index}.id` });
  const titulo = useWatch({ control: form.control, name: `comunicacionProyecto.${props.index}.titulo` });
  const fecha = useWatch({ control: form.control, name: `comunicacionProyecto.${props.index}.fecha` });
  const fuente = useWatch({ control: form.control, name: `comunicacionProyecto.${props.index}.fuente` });
  const normalizedLinks = Array.isArray(links) ? links : [];
  const filledLinks = normalizedLinks.filter((link) => String(link).trim());

  const updateLink = (linkIndex: number, value: string) => {
    const nextLinks = normalizedLinks.map((link, index) => (index === linkIndex ? value : link));
    form.setValue(`comunicacionProyecto.${props.index}.enlaces`, nextLinks, { shouldDirty: true, shouldTouch: true });
  };

  const addLink = () => {
    form.setValue(`comunicacionProyecto.${props.index}.enlaces`, [...normalizedLinks, ''], { shouldDirty: true, shouldTouch: true });
  };

  const removeLink = (linkIndex: number) => {
    form.setValue(
        `comunicacionProyecto.${props.index}.enlaces`,
        normalizedLinks.filter((_, index) => index !== linkIndex),
        { shouldDirty: true, shouldTouch: true }
    );
  };

  return (
      <ItemCard
          title={buildCardTitle(t, t('comunicacionSection.communication'), props.index, titulo, fuente)}
          summary={buildSummary(t, fecha, fuente, filledLinks.length ? `${filledLinks.length} ${filledLinks.length > 1 ? t('comunicacionSection.links_plural') : t('comunicacionSection.link')}` : '')}
          onRemove={props.onRemove}
          defaultOpen={props.defaultOpen}
          actions={!readOnly &&
            <ToggleField
                label={t('comunicacionSection.public')}
                name={`comunicacionProyecto.${props.index}.publico`}
                register={form.register}
            />
          }
      >
        <FieldGrid>
          {props.showIds && hasPersistedId(idComunicacion) ? (
              <NumberField label={t('comunicacionSection.id')} name={`comunicacionProyecto.${props.index}.id`} register={form.register} />
          ) : null}
          <TextField label={t('comunicacionSection.name')} name={`comunicacionProyecto.${props.index}.titulo`} register={form.register} />
          <DateField label={t('comunicacionSection.date')} name={`comunicacionProyecto.${props.index}.fecha`} register={form.register} />
          <TextField label={t('comunicacionSection.source')} name={`comunicacionProyecto.${props.index}.fuente`} register={form.register} />
        </FieldGrid>

        <div className="label">
          <span>{t('comunicacionSection.links')}</span>
          <div className="stack">
            {readOnly ? (
                filledLinks.length > 0 ? (
                    filledLinks.map((link, linkIndex) => (
                        <div key={`${props.index}-link-${linkIndex}`} className="readOnlyLink">
                          <a href={link.startsWith('http') ? link : `https://${link}`} target="_blank" rel="noopener noreferrer">
                            {link}
                          </a>
                        </div>
                    ))
                ) : (
                    <div className="muted small">{t('comunicacionSection.noLinks')}</div>
                )
            ) : (
                <>
                  {normalizedLinks.map((link, linkIndex) => (
                      <div key={`${props.index}-link-${linkIndex}`} className="inlineActions">
                        <input
                            className="input"
                            {...form.register(`comunicacionProyecto.${props.index}.enlaces.${linkIndex}` as const, {
                              onChange: (event) => updateLink(linkIndex, event.target.value),
                            })}
                            defaultValue={link}
                            placeholder="https://..."
                        />

                        <button className="btn btnDanger btnSmall" type="button" onClick={() => removeLink(linkIndex)}>
                          {t('comunicacionSection.remove')}
                        </button>
                      </div>
                  ))}

                  <button className="btn btnGhost" type="button" onClick={addLink}>
                    {t('comunicacionSection.addLink')}
                  </button>
                </>
            )}
          </div>
        </div>
      </ItemCard>
  );
}
